import { useCallback, useEffect, useState } from 'react';

interface ErrorEntry {
  id: number;
  type: 'error' | 'unhandledrejection' | 'network';
  message: string;
  timestamp: string;
}

let nextId = 0;

// Browsers emit these notifications when a ResizeObserver callback changes
// layout again within the same frame. Tabulator's auto-resize/redraw cycle can
// trigger them while a table is being laid out, even though rendering recovers
// on the next frame and no application operation has failed.
const RESIZE_OBSERVER_NOTIFICATIONS = new Set([
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
]);

/**
 * 개발 모드 전용 에러 오버레이.
 * ErrorBoundary가 잡지 못하는 비동기 에러를 화면에 표시한다:
 * - window error (uncaught runtime errors)
 * - unhandledrejection (미처리 Promise rejection)
 *
 * 프로덕션 빌드에서는 렌더링하지 않음.
 */
export default function DevErrorOverlay() {
  const [errors, setErrors] = useState<ErrorEntry[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  const addError = useCallback((type: ErrorEntry['type'], message: string) => {
    const entry: ErrorEntry = {
      id: nextId++,
      type,
      message,
      timestamp: new Date().toLocaleTimeString(),
    };
    setErrors((prev) => [...prev.slice(-19), entry]); // 최대 20개
  }, []);

  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      const message = e.message || String(e.error);

      if (RESIZE_OBSERVER_NOTIFICATIONS.has(message)) {
        e.preventDefault();
        return;
      }

      addError('error', message);
    };

    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      let msg: string;
      if (reason instanceof Error) {
        msg = reason.message;
      } else if (typeof reason === 'string') {
        msg = reason;
      } else {
        msg = JSON.stringify(reason) ?? 'Unknown rejection';
      }
      addError('unhandledrejection', msg);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [addError]);

  if (!import.meta.env.DEV || errors.length === 0) return null;

  const badgeColor: Record<ErrorEntry['type'], string> = {
    error: 'bg-red-600',
    unhandledrejection: 'bg-orange-500',
    network: 'bg-yellow-500',
  };

  return (
    <div className="fixed bottom-4 right-4 z-[99999] max-w-[480px] font-mono text-xs">
      {/* 헤더 */}
      <div className="flex items-center justify-between rounded-t bg-red-900 px-3 py-1.5 text-white">
        <span className="font-bold">DEV Errors ({errors.length})</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((p) => !p)}
            className="hover:text-red-300"
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
          <button type="button" onClick={() => setErrors([])} className="hover:text-red-300">
            Clear
          </button>
        </div>
      </div>

      {/* 에러 목록 */}
      {!collapsed && (
        <div className="max-h-[300px] overflow-auto rounded-b border border-red-900 bg-gray-950 p-2">
          {errors.map((e) => (
            <div
              key={e.id}
              className="mb-1.5 border-b border-gray-800 pb-1.5 last:mb-0 last:border-0 last:pb-0"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`${badgeColor[e.type]} rounded px-1 py-0.5 text-[10px] text-white`}
                >
                  {e.type}
                </span>
                <span className="text-gray-500">{e.timestamp}</span>
              </div>
              <p className="mt-0.5 whitespace-pre-wrap break-all text-red-400">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
